import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateLocalBusinessSchema, generateBreadcrumbSchema } from '@/components/SEO';
import { CheckCircle, Target, Users, TrendingUp, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackBookCall } from '@/lib/gtm';

const values = [
  { icon: Target, title: "Outcome-obsessed", desc: "We measure success by your revenue, not your impressions deck." },
  { icon: Users, title: "Partner, not vendor", desc: "We sit on your side of the table. Same KPI, same coffee." },
  { icon: TrendingUp, title: "Compounding work", desc: "Every sprint makes the next one cheaper, faster and sharper." },
];

const principles = [
  "Everything measured — no guesswork, only data",
  "Everything compounding — last week's work makes next week's cheaper",
  "Outcome-focused — revenue is the only metric that matters",
  "Brand + performance, fused — one team, one P&L, one number",
  "Speed without sloppiness — fast ships, no broken windows",
  "Built in public — clients see what we see, when we see it",
];

export default function About() {
  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="about-page">
      <SEO
        title="About — Creative · Media · Technology Studio"
        description="Wizbang is a creative, media and technology agency. We pair strategists, designers, performance marketers and engineers on the same brief to build revenue systems that compound."
        keywords="about wizbang, creative agency India, media agency, technology agency, growth studio, revenue engineers"
        url="/about"
        image="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80&auto=format&fit=crop"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            generateLocalBusinessSchema(),
            generateBreadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "About", path: "/about" }
            ])
          ]
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial opacity-60" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="eyebrow mb-6">About Wizbang</div>
          <h1 className="font-syne text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0A0A0A] mb-8 max-w-5xl tracking-tight leading-[0.95]">
            We don't do <span className="font-editorial text-[#ff5a1f]">marketing.</span><br />
            We build revenue systems.
          </h1>
          <p className="text-[#3A3A3A] text-lg md:text-xl max-w-2xl leading-relaxed">
            Wizbang is a creative, media and technology agency. Strategists, designers, performance marketers and engineers — same team, same brief, same number on the board.
          </p>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-20 bg-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-5">The story</div>
              <h2 className="font-syne text-3xl md:text-5xl font-bold text-[#0A0A0A] mb-6 tracking-tight">
                Built by people who got tired of <span className="font-editorial text-[#ff5a1f]">vibes-based briefs.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-6 text-[#3A3A3A] leading-relaxed text-base md:text-lg">
              <p>
                Wizbang started as a small frustration. Founders kept asking us why their "award-winning campaign" hadn't moved a single line on the P&L. We didn't have an honest answer — so we changed how agencies work.
              </p>
              <p>
                Today we run as one studio with six disciplines: <span className="font-editorial text-[#ff5a1f]">performance marketing, e-commerce, web, custom CRM, packaging and branding</span>. No silos. No "creative team vs. media team" politics. One shared deck, one shared dashboard.
              </p>
              <p>
                We work with ambitious founders, marketing leaders and operators who want one team accountable for the entire customer journey — from first impression to repeat purchase.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-5">What drives us</div>
            <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] tracking-tight">
              Three things we will <span className="font-editorial text-[#ff5a1f]">never</span> negotiate on.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {values.map((value, idx) => (
              <div key={idx} className="p-8 bg-white border rounded-2xl hover:border-[#ff5a1f] transition-all duration-500 card-tilt"
                style={{ borderColor: 'var(--border)' }}>
                <div className="w-12 h-12 bg-[#ff5a1f]/10 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-[#ff5a1f]" />
                </div>
                <h3 className="font-syne text-xl font-bold text-[#0A0A0A] mb-3 tracking-tight">{value.title}</h3>
                <p className="text-[#5B5B5B] text-sm leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-24 bg-white border-y" style={{ borderColor: 'var(--border)' }} data-testid="principles-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="eyebrow mb-5">Operating principles</div>
          <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-12 tracking-tight">
            How we actually <span className="font-editorial text-[#ff5a1f]">show up.</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((p, idx) => (
              <div key={idx} className="flex items-start gap-4 p-5 border rounded-xl bg-[#FAFAF7]" style={{ borderColor: 'var(--border)' }}>
                <div className="w-8 h-8 rounded-full bg-[#ff5a1f]/15 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-[#ff5a1f]" />
                </div>
                <span className="text-[#0A0A0A] font-medium text-base">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-4xl md:text-6xl font-bold text-[#0A0A0A] mb-6 tracking-tight">
            Like the way we <span className="font-editorial text-[#ff5a1f]">think?</span>
          </h2>
          <p className="text-[#5B5B5B] text-lg mb-12">Let's see how it applies to your brief. 40 minutes, no obligation.</p>
          <Link to="/book-appointment" onClick={() => trackBookCall('about_bottom_cta')}
            className="bg-[#0A0A0A] text-white px-10 py-5 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
            Book a call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
